import { Application, FederatedPointerEvent, Point } from 'pixi.js'
import { isDesktopMode } from '@/scripts/helpers/features'
import { clamp } from '@/scripts/utils/math'
import { INPUT } from '@/scripts/config/constants'
import resizer from './resizer'

let appRef: Application | null = null
let isStopped = true
let isDragging = false

const stageRatio = new Point()
const stageRatioBase = new Point()
const dragStart = new Point()
const stageStart = new Point()
let tickerAttached = false

function init(app: Application, initialRatio: Point): void {
  appRef = app

  stageRatioBase.copyFrom(initialRatio)
  stageRatio.set(0, 0)

  if (!isDesktopMode()) {
    app.stage.on('pointerdown', (e) => onDragStart(e, app))
    app.stage.on('pointerup', onDragEnd)
    app.stage.on('pointerupoutside', onDragEnd)
    app.stage.on('pointermove', (e) => onDragMove(e, app))
  } else {
    // Listen to mouse movement for parallax
    app.stage.on('globalmousemove', onPointerMove)
    // Also listen to pointermove for touch events on tablets in desktop mode
    app.stage.on('pointermove', onPointerMoveTouch)
  }

  if (!tickerAttached) {
    app.ticker.add(updateRatioFromEasedVelocity)
    tickerAttached = true
  }
}

function start(): void {
  isStopped = false
  // optional: if you want “soft” resume only (keep below commented)
  // unlock() // if you want hard unlock together with start()
}

function stop(): void {
  isStopped = true
  isDragging = false
  // optional: if you want “soft” stop only (keep below commented)
  // lock() // if you want hard lock together with stop()
}

function lock(): void {
  // HARD lock: no DOM events to canvas + Pixi event traversal off
  if (!appRef) return
  const view = appRef.renderer.view as HTMLCanvasElement
  view.style.pointerEvents = 'none'
  appRef.stage.eventMode = 'none'
  appRef.stage.interactiveChildren = false
  isStopped = true
  isDragging = false
}

function unlock(): void {
  if (!appRef) return
  const view = appRef.renderer.view as HTMLCanvasElement
  view.style.pointerEvents = '' // restore default
  appRef.stage.eventMode = 'static'
  appRef.stage.interactiveChildren = true
  // don't force "start" here; caller decides:
  // isStopped stays as-is so you can choose between start()/stop() vs lock/unlock()
}

function getRatio(): Point {
  return stageRatio
}

function onPointerMove(e: FederatedPointerEvent): void {
  if (isStopped) return
  const dims = resizer.dimensions
  if (!dims) return

  const { stageW, stageH } = dims
  const x = e.global.x
  const y = e.global.y

  // Only react while the pointer is within the canvas bounds
  if (x < 0 || x > stageW || y < 0 || y > stageH) {
    return
  }

  const rx = x / stageW
  const ry = y / stageH
  stageRatioBase.set(rx, ry)
}

function onPointerMoveTouch(e: FederatedPointerEvent): void {
  // Only handle touch events for parallax in desktop mode
  // Mouse events are handled by globalmousemove
  if (isStopped || e.pointerType !== 'touch') return

  const dims = resizer.dimensions
  if (!dims) return

  const { stageW, stageH } = dims
  const x = e.global.x
  const y = e.global.y

  // Only react while the pointer is within the canvas bounds
  if (x < 0 || x > stageW || y < 0 || y > stageH) {
    return
  }

  const rx = x / stageW
  const ry = y / stageH
  stageRatioBase.set(rx, ry)
}

function updateRatioFromEasedVelocity(): void {
  if (isStopped) return
  stageRatio.x += (stageRatioBase.x - stageRatio.x - 0.5) * INPUT.MOVE_EASE
  stageRatio.y += (stageRatioBase.y - stageRatio.y - 0.5) * INPUT.MOVE_EASE
}

function onDragStart(e: FederatedPointerEvent, app: Application): void {
  if (isStopped) return // ✅ guard while overlay open
  isDragging = true
  dragStart.set(e.global.x, e.global.y)
  stageStart.set(app.stage.x, app.stage.y)
}

function onDragMove(e: FederatedPointerEvent, app: Application): void {
  if (isStopped) return // ✅ guard while overlay open
  if (!isDragging) return
  const dims = resizer.dimensions
  if (!dims) return

  const dx = e.global.x - dragStart.x
  const dy = e.global.y - dragStart.y

  const targetX = clamp(stageStart.x + dx, 2 * dims.stageX, 0)
  const targetY = clamp(stageStart.y + dy, 2 * dims.stageY, 0)

  app.stage.x = targetX
  app.stage.y = targetY
}

function onDragEnd(): void {
  isDragging = false
}

export default { init, start, stop, lock, unlock, getRatio }
